import { useState, useEffect, useRef, useMemo } from 'react';
import {
  ChevronLeft,
  ChevronRight,
  Info,
  Calendar as CalendarIcon,
  Upload,
  Trash2,
  Clock,
} from 'lucide-react';
import { Button } from '../core/Button';
import { Card, CardContent } from '../core/Card';
import { SearchableSelect } from '../core/SearchableSelect';
import { useLatestClients } from '../../graphql/hooks/useUserDashboard';
import { useActiveUsers } from '../../graphql/hooks/useUserDashboard';
import { useTrackings } from '../../graphql/hooks/useUserDashboard';
import { useAuth } from '../../graphql/hooks/useAuth';
import TextInput from '../core/TextInput';
import { useUserHasCar } from '../../graphql/hooks/useUserDashboard';
import { useUserHasEmergencyService } from '../../graphql/hooks/useAdminDashboard';
import {
  useCreateTimeTracking,
  useDeleteTimeTracking,
  useCreateSickTracking,
  useCreateOvertimeTracking,
  useCreateHolidayTracking,
  useOpenHolidays
} from '../../graphql/hooks/useUserDashboard';
import { useUserTrackingsByUser, useDeleteTracking, useWorkDaysBetween } from '../../graphql/hooks/useAdminDashboard';
import toast from "react-hot-toast";
import { useMutation } from '@tanstack/react-query';
import { CREATE_DEVICE_TOKEN } from '../../graphql/mutations/user_dashboard';
import { useGqlClient } from '../../graphql/client';
import { useEmergencyExists } from '../../graphql/hooks/useUserDashboard';
import { useToggleEmergency } from '../../graphql/hooks/useUserDashboard';
import { ConfirmModal } from '../core/Modal';
import { useGetCalenderMonth } from '../../graphql/hooks/useAdminDashboard';

type SimpleTimeEntryProps = {
  selectedUserId: string | null;
  setSelectedUserId: React.Dispatch<React.SetStateAction<string | null>>;
};

export function SimpleTimeEntry({
  selectedUserId,
  setSelectedUserId,
}: SimpleTimeEntryProps) {
  const { userId, userType } = useAuth();
  console.log('userType', userType)
  const isAdmin = userType === 'admin' || userType === 'Superadmin';

  const [selectedDate, setSelectedDate] = useState(new Date());
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [isOnCallDuty, setIsOnCallDuty] = useState(false);
  const [onCallSearch, setOnCallSearch] = useState('');
  const effectiveUserId = selectedUserId || userId;
  const [mutationError, setMutationError] = useState<string | null>(null);
  const [localEmergency, setLocalEmergency] = useState<boolean | null>(null);

  const { data: userHasACar } = useUserHasCar();
  const { data: userHasEmergencyService } = useUserHasEmergencyService(effectiveUserId);
  const { data: openHolidays } = useOpenHolidays({ userId: effectiveUserId });
  const { mutate: createTracking, isPending } = useCreateTimeTracking();
  const { mutate: deleteTracking } = useDeleteTimeTracking();
  const { mutate: createSickTracking } = useCreateSickTracking();
  const { mutate: createOvertimeTracking } = useCreateOvertimeTracking();
  const { mutate: createHolidayTracking } = useCreateHolidayTracking();
  const { mutate: deleteRequest } = useDeleteTracking();

  const [durationError, setDurationError] = useState<string | null>(null);
  const [selectedOnCallUser, setSelectedOnCallUser] = useState<{ id: string; full_name: string; } | null>(null);
  const [vacationRange, setVacationRange] = useState<{ from: Date | null;to: Date | null; }>({from: null, to: null});
  const [showCalendar, setShowCalendar] = useState(false);
  const { data: clients } = useLatestClients();
  const { data: activeUsers } = useActiveUsers();
  const activeUserOptions = useMemo(
    () => (activeUsers ?? []).map((user) => ({ value: user.id, label: user.full_name })),
    [activeUsers]
  );
  const gqlClient = useGqlClient();
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [entryToDelete, setEntryToDelete] = useState<{ id: string; type: string } | null>(null);
  const [deleteReason, setDeleteReason] = useState('');
  const [zeitConflictModalOpen, setZeitConflictModalOpen] = useState(false);

  const createDeviceTokenMutation = useMutation({
    mutationFn: async (token: string) => {
      const res = await gqlClient.request(CREATE_DEVICE_TOKEN, { token });
      return res.createDeviceToken;
    },
    onSuccess: () => {
      console.log("Creating device token in the backend succeeded");
    },
    onError: (error) => {
      console.error("Creating device token in the backend failed:", error);
    },
  });

  const { data: calenderMonth } = useGetCalenderMonth({
    userId: effectiveUserId,
    month: currentMonth.getMonth() + 1,
    year: currentMonth.getFullYear(),
  });

  const highlightedByDate = new Map(
    (calenderMonth ?? []).map((day) => [day.date, day.highlighted])
  );

  useEffect(() => {
    const deviceToken = localStorage.getItem("deviceToken");

    const token = !deviceToken ? 'no local storage' : deviceToken
    createDeviceTokenMutation.mutate(token);
  }, []);

  useEffect(() => {
    if (isAdmin && activeUsers?.length && !selectedUserId) {
      setSelectedUserId(activeUsers[0].id);
    }
  }, [isAdmin, activeUsers, selectedUserId]);

  console.log('isAdmin', isAdmin)
  const formatDateForApi = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');

    return `${year}-${month}-${day}`;
  };

  const formatGermanDateToApi = (value: string) => {
    const [day, month, year] = value.split('.');
    return `${year}-${month}-${day}`;
  };

  const formattedDate = formatDateForApi(selectedDate);

  const { data: emergencyExists, refetch: refetchEmergency } = useEmergencyExists(effectiveUserId, formattedDate);
  const isEmergencyChecked = localEmergency !== null ? localEmergency : (emergencyExists ?? false);

  useEffect(() => {
    setLocalEmergency(null);
    refetchEmergency();
  }, [formattedDate]);


  const { mutate: toggleEmergency } = useToggleEmergency();

  const { data: trackings, refetch: refetchTrackings } = useTrackings({
    date: formattedDate,
  });

  const {
    data: adminTrackings,
    refetch: refetchAdminTrackings,
  } = useUserTrackingsByUser({
    userId: isAdmin ? selectedUserId ? selectedUserId : undefined : undefined,
    month: selectedDate.getMonth() + 1,
    year: selectedDate.getFullYear(),
  })

  const sourceTrackings = isAdmin
    ? adminTrackings
    : trackings

  const refreshTrackings = () => {
    if (isAdmin) {
      refetchAdminTrackings();
    } else {
      refetchTrackings();
    }
  };

  const getInitialFormData = () => ({
    userId: userId,
    type: 'Zeit',
    duration: '0:00',
    note: '',
    client: '',
    isEmergency: false,
    isOnCall: false,
    rides: 0,
    start_night: '20:00',
    end_night: '06:00',

    vacationFrom: '',
    vacationTo: '',
    vacationDays: 1,
    onCallCoverage: selectedOnCallUser,

    sickFrom: '',
    sickTo: '',
    sickDays: 0,
    sickFile: null as File | null,
    sickFilePreview: null as string | null,

    overtimeFrom: '',
    overtimeTo: '',
    overtimeDays: 1,
    overtimeAfternoon: false,
    overtimeMorning: false,
    overtimeCoverage: selectedOnCallUser,
  });

  const [formData, setFormData] = useState(getInitialFormData());

  const vacationFromApi = formData.vacationFrom
    ? formData.vacationFrom.split('.').reverse().join('-')
    : '';
  const vacationToApi = formData.vacationTo
    ? formData.vacationTo.split('.').reverse().join('-')
    : '';

  const { data: workDaysBetween } = useWorkDaysBetween(
    effectiveUserId,
    vacationFromApi,
    vacationToApi,
  );

  const monthNames = [
    'Januar',
    'Februar',
    'März',
    'April',
    'Mai',
    'Juni',
    'Juli',
    'August',
    'September',
    'Oktober',
    'November',
    'Dezember',
  ];

  const dayNames = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];

  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    return new Date(year, month + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1).getDay();
    return firstDay === 0 ? 6 : firstDay - 1;
  };

  const notdienstCheckbox = (id: string) => (
    <div className="flex items-center justify-end gap-2 mb-4 pb-4 border-b border-[#e9e9e9]">
      <input
        type="checkbox"
        id={id}
        checked={isEmergencyChecked}
        onChange={() => {
          setLocalEmergency(!isEmergencyChecked);
          toggleEmergency({
            userId: effectiveUserId,
            date: formattedDate,
          });
        }}
        className="size-4 rounded border-[#e9e9e9] text-[#0084c7] focus:ring-[#0084c7]"
      />
      <label htmlFor={id} className="text-[#1c1c1c] text-[14px]">Notdienst</label>
    </div>
  );

  const mapTrackingTypeToEnum = (
    type: string
  ): "HOLIDAY" | "OVERTIME" | "SICK" | "TIME" => {
    switch (type) {
      case "holiday":
        return "HOLIDAY";
      case "overtime":
        return "OVERTIME";
      case "sick":
        return "SICK";
      case "time":
        return "TIME";
      default:
        throw new Error(`Unknown tracking type: ${type}`);
    }
  };

  useEffect(() => {
    const dateStr = formatDateForApi(selectedDate);

    const dayEntries = sourceTrackings?.filter(
      (entry) => entry.date === dateStr
    );

    const hasEmergencyService = dayEntries?.some(
      (entry) => entry.isEmergency
    );

    setFormData((prev) => ({
      ...prev,
      isOnCall: Boolean(hasEmergencyService),
    }));
  }, [selectedDate, sourceTrackings]);

  const previousMonth = () => {
    setCurrentMonth(
      new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1),
    );
  };

  const nextMonth = () => {
    setCurrentMonth(
      new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1),
    );
  };

  const isSelectedDate = (date: Date): boolean => {
    return (
      date.getDate() === selectedDate.getDate() &&
      date.getMonth() === selectedDate.getMonth() &&
      date.getFullYear() === selectedDate.getFullYear()
    );
  };

  const isToday = (date: Date): boolean => {
    const today = new Date(); 
    return (
      date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear()
    );
  };

  const daysInMonth = getDaysInMonth(currentMonth);
  const firstDay = getFirstDayOfMonth(currentMonth);

  const handleDateClick = (date: Date) => {
    const calculateDaysBetween = (from: Date, to: Date) => {
      const start = new Date(from);
      const end = new Date(to);

      start.setHours(0, 0, 0, 0);
      end.setHours(0, 0, 0, 0);

      if (end < start) return 0;

      let count = 0;
      const current = new Date(start);

      while (current <= end) {
        const day = current.getDay(); // 0 = Sonntag, 6 = Samstag

        if (day !== 0 && day !== 6) {
          count++;
        }

        current.setDate(current.getDate() + 1);
      }

      return count;
    };

    if (!vacationRange.from || vacationRange.to) {
      setVacationRange({ from: date, to: null });
      return;
    }

    const from = date < vacationRange.from ? date : vacationRange.from;
    const to = date < vacationRange.from ? vacationRange.from : date;

    setVacationRange({ from, to });

    const fromStr = formatDateForInput(from);
    const toStr = formatDateForInput(to);
    const days = calculateDaysBetween(from, to);

    if (formData.type === 'Krank') {
      setFormData((prev) => ({
        ...prev,
        sickFrom: fromStr,
        sickTo: toStr,
        sickDays: days,
      }));
    }

    if (formData.type === 'Urlaub') {
      setFormData((prev) => ({
        ...prev,
        vacationFrom: fromStr,
        vacationTo: toStr,
        vacationDays: days,
      }));
    }

    if (formData.type === 'Überstunden') {
      setFormData((prev) => ({
        ...prev,
        overtimeFrom: fromStr,
        overtimeTo: toStr,
        overtimeDays: days,
      }));
    }

    setShowCalendar(false); 
  };

  const getGraphQLErrorMessage = (error: any): string => {
    const message =
      error?.response?.errors?.[0]?.extensions?.debugMessage ||
      error?.response?.errors?.[0]?.message ||
      error?.message ||
      "";

    if (message.includes("There is already a tracking (holiday/overtime/sick) in this period")) {
      return "Ein Eintrag (Urlaub / Überstunden / Krankmeldung) existiert bereits in diesem Zeitraum.";
    }

    if (message.includes("A sick tracking already exists in this period")) {
      return "Krankmeldung existiert bereits in diesem Zeitraum.";
    }

    if (message.startsWith("An diesem Tag ist bereits")) {
      return message;
    }

    return "Vorgang fehlgeschlagen";
  };

  const submitZeitTracking = () => {
    const formatted = formatTime(formData.duration);

    const durationToFloat = (value: string) => {
      const [hours, minutes] = value.split(':').map(Number);
      return hours + minutes / 60;
    };

    createTracking(
      {
        user_id: effectiveUserId,
        date: formattedDate,
        client: formData.client || '',
        duration: durationToFloat(formatted),
        is_night_work: formData.isEmergency,
        start_night: formData.start_night,
        end_night: formData.end_night,
        is_emergency_service: isEmergencyChecked,
        comment: formData.note || null,
        drive: Number(formData.rides || 0),
      },
      {
        onSuccess: () => {
          setFormData(getInitialFormData());
          setMutationError(null);
          refreshTrackings();
          toast.success(
            `Eintrag gespeichert • ${formattedDate}${formData.client ? ` • ${formData.client}` : ""}`,
            {
              duration: 3000,
              style: {
                background: "#f0fdf4",
                border: "1px solid #bbf7d0",
                color: "#15803d",
                padding: "12px 14px",
                borderRadius: "8px",
                fontSize: "14px",
              },
              iconTheme: {
                primary: "#16a34a",
                secondary: "#f0fdf4",
              },
            }
          );
        },
        onError: (error) => {
          setMutationError(getGraphQLErrorMessage(error));
        },
      },
    );
  };

  const handleSubmit = () => {
    const resetForm = () => {
      setFormData(getInitialFormData());
      setVacationRange({ from: null, to: null });
      setSelectedOnCallUser(null);
      setOnCallSearch('');
      setShowCalendar(false);
      setDurationError(null);
    };

    if (formData.type === 'Zeit') {
      const formatted = formatTime(formData.duration);
      if (!isValidDuration(formatted)) {
        setDurationError('Bitte gültige Stunden eingeben');
        return;
      }

      const dateStr = formatDateForApi(selectedDate);
      const dayEntries = sourceTrackings?.filter((entry) => entry.date === dateStr);
      const hasConflict = dayEntries?.some(
        (entry) => entry.type === 'holiday' || entry.type === 'sick'
      );

      if (isAdmin && hasConflict) {
        setZeitConflictModalOpen(true);
        return;
      }

      submitZeitTracking();
      return;
    }

    if (formData.type === 'Krank') {
      if (!formData.sickFrom || !formData.sickTo) {
        alert('Bitte Zeitraum auswählen');
        return;
      }

      createSickTracking(
        {
          user_id: effectiveUserId, 
          start_date: formatGermanDateToApi(formData.sickFrom),
          end_date: formatGermanDateToApi(formData.sickTo),
          comment: formData.note || null,
          file: formData.sickFile || null,
        },
        {
          onSuccess: () => {
            const from = formData.sickFrom;
            const to = formData.sickTo;

            resetForm();
            setMutationError(null);
            refreshTrackings();

            toast.success(
              `Krankmeldung gespeichert • ${from} – ${to}`,
              {
                duration: 5000,
                style: {
                  background: "#f0fdf4",
                  border: "1px solid #bbf7d0",
                  color: "#15803d",
                  padding: "12px 14px",
                  borderRadius: "8px",
                  fontSize: "14px",
                },
                iconTheme: {
                  primary: "#16a34a",
                  secondary: "#f0fdf4",
                },
              }
            );
          },
          onError: (error) => {
            setMutationError(getGraphQLErrorMessage(error));
          },
        },
      );
    }

    if (formData.type === 'Urlaub') {
      if (!formData.vacationFrom || !formData.vacationTo) {
        alert('Bitte Zeitraum auswählen');
        return;
      }

      createHolidayTracking(
        {
          user_id: isAdmin ? selectedUserId : null,
          start_date: formatGermanDateToApi(formData.vacationFrom),
          end_date: formatGermanDateToApi(formData.vacationTo),
          substitute_id: selectedOnCallUser?.id || null,
          comment: formData.note || null,
        },
        {
          onSuccess: () => {
            const from = formData.vacationFrom;
            const to = formData.vacationTo;

            refreshTrackings();
            resetForm();
            setMutationError(null);

            toast.success(
              `Urlaub beantragt • ${from} – ${to}`,
              {
                duration: 5000,
                style: {
                  background: "#f0fdf4",
                  border: "1px solid #bbf7d0",
                  color: "#15803d",
                  padding: "12px 14px",
                  borderRadius: "8px",
                  fontSize: "14px",
                },
                iconTheme: {
                  primary: "#16a34a",
                  secondary: "#f0fdf4",
                },
              }
            );
          },
          onError: (error) => {
            setMutationError(getGraphQLErrorMessage(error));
          },
        },
      );
    }

    if (formData.type === 'Überstunden') {
      if (!formData.overtimeFrom || !formData.overtimeTo) {
        alert('Bitte Zeitraum auswählen');
        return;
      }

      createOvertimeTracking(
        {
          user_id: isAdmin ? selectedUserId : null,
          start_date: formatGermanDateToApi(formData.overtimeFrom),
          end_date: formatGermanDateToApi(formData.overtimeTo),
          substitute_id: selectedOnCallUser?.id || null,
          comment: formData.note || null,
        },
        {
          onSuccess: () => {
            const from = formData.overtimeFrom;
            const to = formData.overtimeTo;

            resetForm();
            setMutationError(null);
            refreshTrackings();

            toast.success(
              `Überstunden gespeichert • ${from} – ${to}`,
              {
                duration: 5000,
                style: {
                  background: "#f0fdf4",
                  border: "1px solid #bbf7d0",
                  color: "#15803d",
                  padding: "12px 14px",
                  borderRadius: "8px",
                  fontSize: "14px",
                },
                iconTheme: {
                  primary: "#16a34a",
                  secondary: "#f0fdf4",
                },
              }
            );
          },
          onError: (error) => {
            setMutationError(getGraphQLErrorMessage(error));
          },
        },
      );
    }

    setShowCalendar(false);
    setDurationError(null);
    setIsOnCallDuty(false);
  };

  const formatSelectedDate = () => {
    const day = selectedDate.getDate();
    const month = monthNames[selectedDate.getMonth()];
    const year = selectedDate.getFullYear();
    return `${day}. ${month} ${year}`;
  };

  const formatDateForInput = (date: Date) => {
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}.${month}.${year}`;
  };

  const handleFileChange = (file: File | null) => {
    if (!file) return;

    const allowedTypes = [
      'image/jpeg',
      'image/png',
      'image/heic',
      'image/heif',
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    ];

    const isAllowed = allowedTypes.includes(file.type) || file.type === '';

    if (!isAllowed) {
      alert('Nur JPEG, PNG, HEIC, PDF oder DOC Dateien erlaubt.');
      return;
    }

    const previewUrl = file.type.startsWith('image/')
      ? URL.createObjectURL(file)
      : null;

    setFormData((prev) => ({
      ...prev,
      sickFile: file,
      sickFilePreview: previewUrl,
    }));
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    handleFileChange(file);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  };

  const getButtonText = () => {
    switch (formData.type) {
      case 'Urlaub':
        return 'Urlaub beantragen';
      case 'Krank':
        return 'Krankmeldung einsenden';
      case 'Überstunden':
        return 'Überstunden buchen';
      default:
        return 'Zeiteintrag buchen';
    }
  };

  const filteredUsers =
    onCallSearch === ''
      ? []
      : activeUsers?.filter((user) =>
          user.full_name.toLowerCase().includes(onCallSearch.toLowerCase()),
        );

  const isInRange = (date: Date) =>
    vacationRange.from &&
    vacationRange.to &&
    date >= vacationRange.from &&
    date <= vacationRange.to;

  const isRangeStart = (date: Date) =>
    vacationRange.from &&
    date.toDateString() === vacationRange.from.toDateString();

  const isRangeEnd = (date: Date) =>
    vacationRange.to && date.toDateString() === vacationRange.to.toDateString();

  const renderCalendar = () => {
    const daysInMonth = getDaysInMonth(currentMonth);
    const firstDay = getFirstDayOfMonth(currentMonth);

    return (
      <>
        <div className="flex items-center justify-between mb-4">
          <button onClick={previousMonth}>
            <ChevronLeft className="size-5 text-[#6b6b6b]" />
          </button>

          <div className="text-sm font-medium">
            {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
          </div>

          <button onClick={nextMonth}>
            <ChevronRight className="size-5 text-[#6b6b6b]" />
          </button>
        </div>

        {/* Weekdays */}
        <div className="grid grid-cols-7 text-center text-xs text-[#6b6b6b] mb-2">
          {dayNames.map((d) => (
            <div key={d}>{d}</div>
          ))}
        </div>

        {/* Days */}
        <div className="grid grid-cols-7 gap-1">
          {Array.from({ length: firstDay }).map((_, i) => (
            <div key={i} />
          ))}

          {Array.from({ length: daysInMonth }).map((_, i) => {
            const date = new Date(
              currentMonth.getFullYear(),
              currentMonth.getMonth(),
              i + 1,
            );

            return (
              <button
                key={i}
                onClick={() => handleDateClick(date)}
                className={`h-9 rounded-md text-sm transition-colors
                ${
                  isRangeStart(date) || isRangeEnd(date)
                    ? 'bg-[#0084c7] text-white'
                    : isInRange(date)
                      ? 'bg-[#0084c7]/20 text-[#0084c7]'
                      : 'hover:bg-[#f5f5f5]'
                }
              `}
              >
                {i + 1}
              </button>
            );
          })}
        </div>
      </>
    );
  };

  const resetDateFields = () => {
    setVacationRange({ from: null, to: null });

    setFormData((prev) => ({
      ...prev,
      vacationFrom: '',
      vacationTo: '',
      vacationDays: 1,

      sickFrom: '',
      sickTo: '',
      sickDays: 0,

      overtimeFrom: '',
      overtimeTo: '',
      overtimeDays: 1,
    }));
  };

  const isValidDuration = (value: string) => {
    // HH:mm where HH = 0–23 (or more if you want), mm = 00–59
    return /^(\d+):([0-5][0-9])$/.test(value);
  };

  const formatTime = (input: string) => {
    if (!input) {
      return '';
    }
    const value = input.replace(',', '.').trim();

    let hours;
    let minutes;

    // Fall 1: Format mit Doppelpunkt (z.B. 6:00 oder 6:5)
    if (value.includes(':')) {
      const [h, m = '0'] = value.split(':');
      hours = parseInt(h, 10);
      minutes = parseInt(m, 10);
    }
    // Fall 2: Dezimalzahl (z.B. 6.5)
    else {
      const number = parseFloat(value);
      if (isNaN(number)) return '';

      hours = Math.floor(number);
      minutes = Math.round((number - hours) * 60);
    }

    if (isNaN(hours) || isNaN(minutes)) return '';

    // Falls Minuten >= 60 (Sicherheitscheck)
    if (minutes >= 60) {
      hours += Math.floor(minutes / 60);
      minutes = minutes % 60;
    }

    return `${hours}:${minutes.toString().padStart(2, '0')}`;
  };

  const availableHolidays = openHolidays?.openHoliday ?? 0;
  const effectiveVacationDays = workDaysBetween ?? formData.vacationDays ?? 0;
  const remainingHolidays = Math.max(availableHolidays - effectiveVacationDays, 0);

  return (
    <div className="bg-[#f5f5f5] min-h-screen p-4 md:p-8">
      <div className="max-w-[1400px] mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div>
            <Card>
              <CardContent>
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-[#1c1c1c] text-[18px] font-medium">
                    Neuer Zeiteintrag:
                  </h2>
                  <div className="flex items-center gap-1.5 bg-[#0084c7]/10 text-[#0084c7] text-[13px] font-medium px-3 py-1.5 rounded-full">
                    <CalendarIcon className="size-3.5" />
                    <span>{formatSelectedDate()}</span>
                  </div>
                </div>

                <div className="space-y-5">
                  <div>
                    <label className="block text-[#1c1c1c] text-[14px] font-medium mb-2">
                      Typ:
                    </label>
                    <select
                      value={formData.type}
                      onChange={(e) => {
                        const newType = e.target.value;

                        resetDateFields();
                        setFormData((prev) => ({
                          ...prev,
                          type: newType,
                        }));

                        setShowCalendar(false);
                        setMutationError(null);
                      }}
                      className="form-select w-full h-[44px] px-4 border border-[#e9e9e9] rounded-[8px] bg-white text-[#1c1c1c] focus:outline-none focus:ring-2 focus:ring-[#0084c7] focus:border-transparent"
                    >
                      <option value="Zeit">Zeit</option>
                      <option value="Urlaub">Urlaub</option>
                      <option value="Krank">Krank</option>
                      <option value="Überstunden">Überstunden</option>
                    </select>
                  </div>

                  {formData.type === 'Urlaub' ? (
                    <>
                      <div className="relative">
                        <label className="block text-[#1c1c1c] text-[14px] font-medium mb-2">
                          Urlaub von - bis:
                        </label>

                        <input
                          type="text"
                          readOnly
                          value={
                            vacationRange.from && vacationRange.to
                              ? `${formatDateForInput(vacationRange.from)} - ${formatDateForInput(vacationRange.to)}`
                              : 'Datum auswählen'
                          }
                          onClick={() => {
                            setShowCalendar(prev => !prev);
                            setVacationRange({ from: null, to: null });
                          }}
                          className="w-full box-border h-[44px] px-4 pr-10 border border-[#e9e9e9] rounded-[8px]
                                    bg-white text-[#1c1c1c] cursor-pointer
                                    focus:outline-none focus:ring-2 focus:ring-[#0084c7]"
                        />

                        <CalendarIcon className="absolute right-3 top-2/3 -translate-y-1/2 size-5 text-[#6b6b6b] pointer-events-none" />

                        {showCalendar && (
                          <div className="absolute z-30 mt-2 w-full box-border md:w-[320px] bg-white border border-[#e9e9e9] rounded-[12px] shadow-lg p-4">
                            {renderCalendar()}
                          </div>
                        )}
                      </div>
                        <div>
                          <label className="block text-[#1c1c1c] text-[14px] font-medium mb-2">
                            Urlaubstage:
                          </label>

                          <div className="h-[44px] px-4 border border-[#e9e9e9] rounded-[8px] bg-[#f5f5f5] text-[#1c1c1c] flex items-center">
                            {formData.vacationDays} Tage
                          </div>

                          <div className="mt-3 border border-[#e9e9e9] rounded-[10px] bg-white p-3">
                            <div className="flex items-center justify-between text-[13px] text-[#6b6b6b]">
                              <span>Offene Urlaubstage</span>
                              <span className="text-[#1c1c1c] font-medium">
                                {openHolidays?.openHoliday ?? '—'} Tage
                              </span>
                            </div>

                            {openHolidays?.carriedOverHoliday !== undefined && openHolidays.carriedOverHoliday > 0 && (
                              <div className="flex items-center justify-between text-[13px] text-[#6b6b6b] mt-1">
                                <span>Übertrag Vorjahr</span>
                                <span className="text-[#1c1c1c] font-medium">
                                  {openHolidays.carriedOverHoliday} Tage
                                </span>
                              </div>
                            )}

                            <div className="flex items-center justify-between text-[13px] text-[#6b6b6b] mt-1">
                              <span>Ausgewählt</span>
                              <span className="text-[#1c1c1c] font-medium">
                                -{workDaysBetween ?? formData.vacationDays} Tage
                              </span>
                            </div>

                            <div className="flex items-center justify-between text-[13px] mt-2 pt-2 border-t border-[#e9e9e9]">
                              <span className="text-[#6b6b6b]">Verbleibend</span>
                              <span
                                className={`font-medium ${
                                  openHolidays !== undefined && remainingHolidays === 0 && effectiveVacationDays > 0
                                    ? 'text-red-600'
                                    : 'text-[#1c1c1c]'
                                }`}
                              >
                                {openHolidays === undefined ? '—' : `${remainingHolidays} Tage`}
                              </span>
                            </div>

                            {openHolidays !== undefined && (availableHolidays - formData.vacationDays) < 0 && (
                              <p className="mt-2 text-[12px] text-red-600">
                                Du hast mehr Tage ausgewählt als verfügbar.
                              </p>
                            )}
                          </div>
                        </div>

                      <div className="relative">
                        <label className="block text-[#1c1c1c] text-[14px] font-medium mb-2">
                          Notdienstvertretung:
                        </label>

                        <TextInput
                          type="text"
                          value={onCallSearch}
                          onChange={(e) => {
                            setOnCallSearch(e.currentTarget.value);
                            setSelectedOnCallUser(null);
                          }}
                          placeholder="Name eingeben…"
                        />

                        {onCallSearch && !selectedOnCallUser && (
                          <div className="absolute z-20 mt-1 w-full border border-[#e9e9e9] rounded-[8px] bg-white max-h-[200px] overflow-y-auto shadow-sm">
                            {filteredUsers?.length === 0 ? (
                              <div className="p-3 text-[#6b6b6b] text-sm">
                                Keine Ergebnisse
                              </div>
                            ) : (
                              filteredUsers?.map((user) => (
                                <div
                                  key={user.id}
                                  className="px-4 py-2 cursor-pointer hover:bg-blue-50"
                                  onClick={() => {
                                    setOnCallSearch(user.full_name);
                                    setSelectedOnCallUser(user);
                                  }}
                                >
                                  {user.full_name}
                                </div>
                              ))
                            )}
                          </div>
                        )}
                      </div>
                    </>
                  ) : formData.type === 'Krank' ? (
                    <>
                      <div>
                        <label className="block text-[#1c1c1c] text-[14px] font-medium mb-2">
                          Krank von - bis:
                        </label>
                        <div className="relative">
                          <input
                            type="text"
                            readOnly
                            value={
                              formData.sickFrom && formData.sickTo
                                ? `${formData.sickFrom} - ${formData.sickTo}`
                                : 'Datum auswählen'
                            }
                            onClick={() => {
                              setShowCalendar(prev => !prev);
                              setVacationRange({ from: null, to: null });
                            }}
                            className="w-full box-border h-[44px] px-4 pr-10 border border-[#e9e9e9] rounded-[8px] bg-white text-[#1c1c1c] cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#0084c7]"
                          />
                          <CalendarIcon className="absolute right-3 top-1/2 -translate-y-1/2 size-5 text-[#6b6b6b] pointer-events-none" />
                          {showCalendar && (
                            <div className="absolute z-30 mt-2 w-[320px] bg-white border border-[#e9e9e9] rounded-[12px] shadow-lg p-4">
                              {renderCalendar()}
                            </div>
                          )}
                        </div>
                      </div>

                      <div>
                        <label className="block text-[#1c1c1c] text-[14px] font-medium mb-2">
                          Gesamt:
                        </label>
                        <div className="h-[44px] px-4 border border-[#e9e9e9] rounded-[8px] bg-[#f5f5f5] text-[#1c1c1c] flex items-center">
                          {formData.sickDays} Tage
                        </div>
                      </div>

                      <div>
                        <div
                          onDrop={handleDrop}
                          onDragOver={handleDragOver}
                          className="border-2 border-dashed border-[#e9e9e9] rounded-[8px] p-6 text-center bg-white hover:bg-[#f5f5f5] transition-colors cursor-pointer relative"
                        >
                          {!formData.sickFile && (
                            <input
                              type="file"
                              accept=".jpg,.jpeg,.png,.heic,.heif,.pdf,.doc,.docx"
                              onChange={(e) =>
                                handleFileChange(e.target.files?.[0] || null)
                              }
                              className="absolute inset-0 opacity-0 cursor-pointer"
                            />
                          )}

                          {!formData.sickFile && (
                            <>
                              <Upload
                                className="size-12 text-[#9ca3af] mx-auto mb-3"
                                strokeWidth={1.5}
                              />
                              <p className="text-[#9ca3af] text-[14px]">
                                Drag & drop oder klicken zum Hochladen
                              </p>
                            </>
                          )}

                          {formData.sickFile && (
                            <div className="flex flex-col items-center gap-3">
                              {formData.sickFilePreview && (
                                <img
                                  src={formData.sickFilePreview}
                                  alt="preview"
                                  className="max-h-[180px] rounded-md object-contain"
                                />
                              )}

                              {!formData.sickFilePreview && (
                                <div className="text-sm text-[#1c1c1c] font-medium">
                                  {formData.sickFile.name}
                                </div>
                              )}

                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setFormData((prev) => ({
                                    ...prev,
                                    sickFile: null,
                                    sickFilePreview: null,
                                  }));
                                }}
                                className="text-red-500 text-sm hover:underline"
                              >
                                Datei entfernen
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    </>
                  ) : formData.type === 'Überstunden' ? (
                    <>
                      <div>
                        <label className="block text-[#1c1c1c] text-[14px] font-medium mb-2">
                          Überstunden von - bis:
                        </label>
                        <div className="relative">
                          <input
                            type="text"
                            readOnly
                            value={
                              formData.overtimeFrom && formData.overtimeTo
                                ? `${formData.overtimeFrom} - ${formData.overtimeTo}`
                                : 'Datum auswählen'
                            }
                            onClick={() => {
                              setShowCalendar(prev => !prev);
                              setVacationRange({ from: null, to: null });
                            }}
                            className="w-full box-border h-[44px] px-4 pr-10 border border-[#e9e9e9] rounded-[8px] bg-white text-[#1c1c1c] cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#0084c7]"
                          />
                          <CalendarIcon className="absolute right-3 top-1/2 -translate-y-1/2 size-5 text-[#6b6b6b] pointer-events-none" />
                          {showCalendar && (
                            <div className="absolute z-30 mt-2 w-[320px] bg-white border border-[#e9e9e9] rounded-[12px] shadow-lg p-4">
                              {renderCalendar()}
                            </div>
                          )}
                        </div>
                      </div>

                      <div>
                        <label className="block text-[#1c1c1c] text-[14px] font-medium mb-2">
                          Überstundenstage:
                        </label>
                        <div className="h-[44px] px-4 border border-[#e9e9e9] rounded-[8px] bg-[#f5f5f5] text-[#1c1c1c] flex items-center">
                          {formData.overtimeDays} Tage
                        </div>
                      </div>

                      <div className="relative">
                        <label className="block text-[#1c1c1c] text-[14px] font-medium mb-2">
                          Notdienstvertretung:
                        </label>

                        <TextInput
                          type="text"
                          value={onCallSearch}
                          onChange={(e) => {
                            setOnCallSearch(e.currentTarget.value);
                            setSelectedOnCallUser(null);
                          }}
                          placeholder="Name eingeben…"
                        />

                        {onCallSearch && !selectedOnCallUser && (
                          <div className="absolute z-20 mt-1 w-full border border-[#e9e9e9] rounded-[8px] bg-white max-h-[200px] overflow-y-auto shadow-sm">
                            {filteredUsers?.length === 0 ? (
                              <div className="p-3 text-[#6b6b6b] text-sm">
                                Keine Ergebnisse
                              </div>
                            ) : (
                              filteredUsers?.map((user) => (
                                <div
                                  key={user.id}
                                  className="px-4 py-2 cursor-pointer hover:bg-blue-50"
                                  onClick={() => {
                                    setOnCallSearch(user.full_name);
                                    setSelectedOnCallUser(user);
                                  }}
                                >
                                  {user.full_name}
                                </div>
                              ))
                            )}
                          </div>
                        )}
                      </div>
                    </>
                  ) : (
                    <>
                      <div>
                        <label className="block text-[#1c1c1c] text-[14px] font-medium mb-2">
                          Kunde:
                        </label>
                        <input
                          type="text"
                          list="latest-clients"
                          value={formData.client}
                          onChange={(e) =>
                            setFormData({ ...formData, client: e.target.value })
                          }
                          placeholder="Hier Kunde eintragen..."
                          className="w-full box-border h-[44px] px-4 border border-[#e9e9e9] rounded-[8px] bg-white text-[#1c1c1c] placeholder:text-[#9ca3af] focus:outline-none focus:ring-2 focus:ring-[#0084c7] focus:border-transparent"
                        />

                        <datalist id="latest-clients">
                          {clients?.clients?.map((client) => (
                            <option key={client} value={client} />
                          ))}
                        </datalist>
                      </div>

                      <div>
                        <label className="block text-[#1c1c1c] text-[14px] font-medium mb-2">
                          Dauer:
                        </label>
                        <input
                          type="text"
                          value={formData.duration}
                          onFocus={() => {
                            if (formData.duration === '0:00') {
                              setFormData({ ...formData, duration: '' });
                            }
                          }}
                          onChange={(e) => {
                            const value = e.target.value;
                            setFormData({ ...formData, duration: value });

                            if (!value) {
                              setDurationError(null);
                            } else if (!isValidDuration(value)) {
                              setDurationError(
                                'Bitte Stunden im Format 5 oder 5:30 eingeben',
                              );
                            } else {
                              setDurationError(null);
                            }
                          }}
                          onBlur={() => {
                            if (!formData.duration) return;

                            const formatted = formatTime(formData.duration);

                            if (!formatted || !isValidDuration(formatted)) {
                              setDurationError(
                                'Bitte Stunden im Format 5 oder 5:30 eingeben',
                              );
                              return;
                            }

                            setFormData({ ...formData, duration: formatted });
                            setDurationError(null);
                          }}
                          className={`w-full box-border h-[44px] px-4 border rounded-[8px] bg-white text-[#1c1c1c] focus:outline-none focus:ring-2
                              ${durationError ? 'border-red-400 focus:ring-red-300' : 'border-[#e9e9e9] focus:ring-[#0084c7]'}
                            `}
                        />
                        {durationError && (
                          <p className="text-red-500 text-[12px] mt-1">
                            {durationError}
                          </p>
                        )}
                      </div>

                      <div>
                        <label className="block text-[#1c1c1c] text-[14px] font-medium mb-2">
                          Bemerkung:
                        </label>
                        <textarea
                          value={formData.note}
                          onChange={(e) =>
                            setFormData({ ...formData, note: e.target.value })
                          }
                          rows={4}
                          className="w-full box-border px-4 py-3 border border-[#e9e9e9] rounded-[8px] bg-white text-[#1c1c1c] focus:outline-none focus:ring-2 focus:ring-[#0084c7] focus:border-transparent resize-none"
                        />
                      </div>

                      {userHasACar && (
                        <div>
                          <label className="block text-[#1c1c1c] text-[14px] font-medium mb-2">
                            Fahrten:
                          </label>
                          <select
                            value={formData.rides}
                            onChange={(e) => {
                              setFormData({
                                ...formData,
                                rides: Number(e.target.value),
                              });
                            }}
                            className="form-select w-full h-[44px] px-4 border border-[#e9e9e9] rounded-[8px] bg-white text-[#1c1c1c] focus:outline-none focus:ring-2 focus:ring-[#0084c7] focus:border-transparent"
                          >
                            <option value={0}>0</option>
                            <option value={0.5}>0.5</option>
                            <option value={1}>1</option>
                          </select>
                        </div>
                      )}

                      <div className="flex items-center gap-2 pt-2">
                        <input
                          type="checkbox"
                          id="emergency"
                          checked={formData.isEmergency}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              isEmergency: e.target.checked,
                            })
                          }
                          className="size-4 rounded border-[#e9e9e9] text-[#0084c7] focus:ring-[#0084c7]"
                        />
                        <label
                          htmlFor="emergency"
                          className="text-[#1c1c1c] text-[14px] flex items-center gap-1.5"
                        >
                          Nachtarbeit
                          <div className="relative group">
                            <Info className="size-4 text-[#6b6b6b] cursor-help" />
                            <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 hidden group-hover:block w-[280px] z-10">
                              <div className="bg-[#1c1c1c] text-white text-[13px] px-4 py-3 rounded-[8px] shadow-lg">
                                Nachtarbeit nur gültig zwischen{' '}
                                {formData.start_night} Uhr und{' '}
                                {formData.end_night} Uhr
                                <div className="absolute left-1/2 -translate-x-1/2 top-full w-0 h-0 border-l-[6px] border-r-[6px] border-t-[6px] border-l-transparent border-r-transparent border-t-[#1c1c1c]"></div>
                              </div>
                            </div>
                          </div>
                        </label>
                      </div>

                      {formData.isEmergency && (
                        <div className="grid grid-cols-2 gap-4">
                          <div className="relative">
                            <input
                              type="time"
                              value={formData.start_night}
                              onChange={(e) =>
                                setFormData({
                                  ...formData,
                                  start_night: e.target.value,
                                })
                              }
                              className="w-full box-border h-[44px] px-4 pr-10 border border-[#e9e9e9] rounded-[8px] bg-white text-[#1c1c1c] focus:outline-none focus:ring-2 focus:ring-[#0084c7] focus:border-transparent"
                            />
                            <Clock className="absolute right-3 top-1/2 -translate-y-1/2 size-5 text-[#6b6b6b] pointer-events-none" />
                          </div>

                          <div className="relative">
                            <input
                              type="time"
                              value={formData.end_night}
                              onChange={(e) =>
                                setFormData({
                                  ...formData,
                                  end_night: e.target.value,
                                })
                              }
                              className="w-full box-border h-[44px] px-4 pr-10 border border-[#e9e9e9] rounded-[8px] bg-white text-[#1c1c1c] focus:outline-none focus:ring-2 focus:ring-[#0084c7] focus:border-transparent"
                            />
                            <Clock className="absolute right-3 top-1/2 -translate-y-1/2 size-5 text-[#6b6b6b] pointer-events-none" />
                          </div>
                        </div>
                      )}

                      {userHasEmergencyService && (
                        <div className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            id="onCall"
                            checked={isEmergencyChecked}
                            onChange={() => {
                              setLocalEmergency(!isEmergencyChecked); 
                              toggleEmergency({
                                userId: isAdmin ? selectedUserId ?? undefined : undefined,
                                date: formattedDate,
                              });
                            }}
                            className="size-4 rounded border-[#e9e9e9] text-[#0084c7] focus:ring-[#0084c7]"
                          />
                          <label
                            htmlFor="onCall"
                            className="text-[#1c1c1c] text-[14px] flex items-center gap-1.5"
                          >
                            Notdienst
                          </label>
                        </div>
                      )}
                    </>
                  )}
                  {mutationError && (
                    <div className="mb-4 p-3 rounded-[8px] bg-red-50 border border-red-200 text-red-600 text-[14px]">
                      {mutationError}
                    </div>
                  )}
                  <div className="pt-4">
                    <Button
                      variant="primary"
                      onClick={handleSubmit}
                      className="w-full"
                      disabled={isPending}
                    >
                      {isPending ? 'Speichern...' : getButtonText()}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          <div>
            <Card>
              {isAdmin && activeUsers?.length && (
                <div className="m-4">
                  <SearchableSelect
                    value={selectedUserId ?? activeUsers[0]?.id}
                    onChange={(id) => id && setSelectedUserId(id)}
                    options={activeUserOptions}
                    placeholder="Mitarbeiter auswählen"
                    searchPlaceholder="Mitarbeiter suchen..."
                    noResultsText="Keine Mitarbeiter gefunden"
                    className="w-full h-[44px]"
                  />
                </div>
              )}
              <CardContent>
                {formData.type === 'Urlaub' && userHasEmergencyService && notdienstCheckbox('onCallDuty')}

                {formData.type === 'Krank' && userHasEmergencyService && notdienstCheckbox('onCallDutySick')}

                {formData.type === 'Überstunden' && userHasEmergencyService && notdienstCheckbox('onCallDutyOvertime')}

                <div className="flex items-center justify-between mb-6">
                  <button
                    onClick={previousMonth}
                    className="p-2 hover:bg-[#f5f5f5] rounded-[8px] transition-colors"
                  >
                    <ChevronLeft className="size-5 text-[#6b6b6b]" />
                  </button>
                  <h3 className="text-[#1c1c1c] text-[16px] font-medium">
                    {monthNames[currentMonth.getMonth()]}{' '}
                    {currentMonth.getFullYear()}
                  </h3>
                  <button
                    onClick={nextMonth}
                    className="p-2 hover:bg-[#f5f5f5] rounded-[8px] transition-colors"
                  >
                    <ChevronRight className="size-5 text-[#6b6b6b]" />
                  </button>
                </div>

                <div className="mb-6">
                  <div className="grid grid-cols-7 gap-1 mb-2">
                    {dayNames.map((day) => (
                      <div
                        key={day}
                        className="text-center text-[#6b6b6b] text-[13px] font-medium py-2"
                      >
                        {day}
                      </div>
                    ))}
                  </div>

                  {/* Calendar Days */}
                  <div className="grid grid-cols-7 gap-1">
                    {Array.from({ length: firstDay }).map((_, index) => (
                      <div key={`empty-${index}`} className="aspect-square" />
                    ))}

                    {Array.from({ length: daysInMonth }).map((_, index) => {
                      const day = index + 1;
                      const date = new Date(
                        currentMonth.getFullYear(),
                        currentMonth.getMonth(),
                        day,
                      );
                      const isSelected = isSelectedDate(date);
                      const isTodayDate = isToday(date);
                      const highlight = highlightedByDate.get(formatDateForApi(date));

                      return (
                        <button
                          key={day}
                          onClick={() => setSelectedDate(date)}
                          className={`aspect-square flex flex-col items-center justify-center gap-1 text-[14px] rounded-[8px] transition-colors ${
                            isSelected
                              ? 'bg-[#0084c7] text-white font-medium'
                              : isTodayDate
                                ? 'bg-[#0084c7]/10 text-[#0084c7] font-medium'
                                : 'text-[#6b6b6b] hover:bg-[#f5f5f5]'
                          }`}
                        >
                          <span>{day}</span>
                          {highlight && !isSelected && (
                            <span
                              className={`h-[3px] w-5 rounded-full ${
                                highlight === 'red' ? 'bg-red-500' : 'bg-green-500'
                              }`}
                            />
                          )}
                        </button>
                      );
                    })}

                    {Array.from({
                      length: 7 - ((firstDay + daysInMonth) % 7 || 7),
                    }).map((_, index) => (
                      <div
                        key={`empty-end-${index}`}
                        className="aspect-square"
                      />
                    ))}
                  </div>
                </div>

                <div className="space-y-4">
                  {(() => {
                    const dateStr = formatDateForApi(selectedDate);
                    const dayEntries = sourceTrackings?.filter(
                      (entry) => entry.date === dateStr,
                    );

                    if (dayEntries?.length === 0) {
                      return (
                        <div className="bg-[#f5f5f5] rounded-[8px] p-4 text-center">
                          <p className="text-[#6b6b6b] text-[14px]">
                            Noch keine Zeiteinträge für den{' '}
                            {selectedDate.getDate()}.{' '}
                            {monthNames[selectedDate.getMonth()]} verbucht.
                          </p>
                        </div>
                      );
                    }

                    return dayEntries?.map((entry) => (
                      <div
                        key={entry.id}
                        className="bg-white border border-[#e9e9e9] rounded-[8px] overflow-hidden"
                      >
                        {/* DESKTOP TABLE */}
                        <div className="hidden md:block">
                          <table className="w-full table-fixed">
                            <thead>
                              <tr className="bg-[#eef2ff]">
                                <th className="text-left px-2 py-2 text-[14px] w-[30%]">Kunde</th>
                                <th className="text-left px-2 py-2 text-[14px] w-[20%]">Dauer</th>
                                <th className="text-left px-2 py-2 text-[14px] w-[42%]">Kommentar</th>
                                <th className="w-[8%]" />
                              </tr>
                            </thead>

                            <tbody>
                              <tr>
                                <td className="px-2 py-2 break-all">{entry.client}</td>
                                <td className="px-2 py-2">{entry.duration}</td>
                                <td className="px-2 py-2">{entry.comment}</td>

                                {!entry.locked && (
                                  <td className="px-1 py-1">
                                    <button
                                      onClick={() => {
                                        if (entry.type === 'holiday' || entry.type === 'overtime') {
                                          setEntryToDelete({ id: entry.id, type: entry.type });
                                          setDeleteReason('');
                                          setDeleteModalOpen(true);
                                        } else {
                                          deleteRequest(
                                            { id: entry.id, type: mapTrackingTypeToEnum(entry.type) },
                                            {
                                              onError: () => {
                                                toast.error("Eintrag konnte nicht gelöscht werden.");
                                              },
                                            }
                                          );
                                        }
                                      }}
                                      className="w-8 h-8 flex items-center justify-center hover:bg-[#fef2f2] rounded"
                                    >
                                      <Trash2 className="size-3 text-[#ef4444]" />
                                    </button>
                                  </td>
                                )}
                              </tr>
                            </tbody>
                          </table>
                        </div>
                        {/* MOBILE CARD */}
                        <div className="md:hidden bg-white p-4 space-y-3">
                          
                          <div>
                            <p className="text-[12px] text-[#6b6b6b]">Kunde</p>
                            <p className="text-[14px] text-[#1e293b] font-medium">
                              {entry.client}
                            </p>
                          </div>

                          <div className="flex justify-between">
                            <div>
                              <p className="text-[12px] text-[#6b6b6b]">Dauer</p>
                              <p className="text-[14px]">{entry.duration}</p>
                            </div>

                            {!entry.locked && (
                              <button
                                onClick={() =>
                                  deleteRequest({
                                    id: entry.id,
                                    type: mapTrackingTypeToEnum(entry.type),
                                  })
                                }
                                className="w-9 h-9 flex items-center justify-center bg-[#fef2f2] rounded-lg"
                              >
                                <Trash2 className="size-4 text-[#ef4444]" />
                              </button>
                            )}
                          </div>

                          {entry.comment && (
                            <div>
                              <p className="text-[12px] text-[#6b6b6b]">Kommentar</p>
                              <p className="text-[14px]">{entry.comment}</p>
                            </div>
                          )}
                        </div>
                      </div>
                    ));
                  })()}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        <div className="mt-6 text-center">
          <p className="text-[#9ca3af] text-[12px]">
            © 2026 ENT-TRO GmbH by{' '}
            <a
              href="https://curvedesign.de"
              target="_blank"
              rel="noopener noreferrer"
              className="underline text-inherit"
            >
              CurveDesign GmbH
            </a>
          </p>
        </div>
      </div>
      <ConfirmModal
        isOpen={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        title="Eintrag löschen"
        message={
          <>
            <p>Möchten Sie den Eintrag wirklich löschen?</p>
            <div className="mt-3 space-y-1">
              <label className="text-[14px] text-[#6b6b6b]">
                Grund (optional)
              </label>
              <textarea
                value={deleteReason}
                onChange={(e) => setDeleteReason(e.target.value)}
                rows={3}
                placeholder="Bitte geben Sie einen Grund an..."
                className="w-full box-border px-3 py-2 border border-[#e0e0e0] rounded-lg text-[14px] text-[#1c1c1c] focus:outline-none focus:ring-2 focus:ring-[#0084c7] focus:border-transparent resize-none"
              />
            </div>
          </>
        }
        onConfirm={() => {
          if (entryToDelete) {
            deleteRequest(
              {
                id: entryToDelete.id,
                type: mapTrackingTypeToEnum(entryToDelete.type),
                reason: deleteReason,
              },
              {
                onError: () => {
                  toast.error("Eintrag konnte nicht gelöscht werden.", {
                    duration: 5000,
                    style: {
                      background: "#fef2f2",
                      border: "1px solid #fecaca",
                      color: "#dc2626",
                      padding: "12px 14px",
                      borderRadius: "8px",
                      fontSize: "14px",
                    },
                    iconTheme: {
                      primary: "#dc2626",
                      secondary: "#fef2f2",
                    },
                  });
                },
              }
            );
            setDeleteModalOpen(false);
          }
        }}
      />
      <ConfirmModal
        isOpen={zeitConflictModalOpen}
        onClose={() => setZeitConflictModalOpen(false)}
        title="Eintrag trotzdem buchen?"
        message="Für diesen Tag ist bereits ein Urlaub oder eine Krankmeldung eingetragen. Möchten Sie den Zeiteintrag trotzdem buchen?"
        onConfirm={() => {
          setZeitConflictModalOpen(false);
          submitZeitTracking();
        }}
      />
    </div>
  );
}